/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import { useTranslation } from 'react-i18next';
import * as R from 'ramda';

import {
  closeModalPage,
  openModalPage,
  previousModalPage,
} from 'actions/modalPage';
import { saveCustomer } from 'actions/customerSearch';
import CloseButton from 'components/CustomButtons/CloseButton';
import SaveButton from 'components/CustomButtons/SaveButton';
import { modalPages as mp } from 'constants/modalPage';
import {
  getSelectedCustomer,
  getUseCustomerRegistry,
} from 'reducers/customerSearch';
import { getCanAddCustomers } from 'reducers/Login';
import {
  getPrefillHomeStoreOnCustomerCreation,
  getSetting,
} from 'reducers/configs/settings';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { addWarning } from 'actions/Error';
import { getGeneralCustomerSettings } from 'reducers/cafaConfigs';

import { CUSTOMER_TYPES } from '../../../Settings/views/Customers/types';

import { companyInitState, personInitState } from './formShapes';
import CustomerForm from './CustomerForm';
import './CreateCustomer.sass';

const defaultCustomerData = {
  addresses: [],
};

const CreateCustomer = ({
  newCustomer,
  newCustomerData = defaultCustomerData,
  customer: inputCustomer,
}) => {
  const selectedCustomer = useSelector(getSelectedCustomer);
  const customer = inputCustomer || selectedCustomer;

  const dispatch = useDispatch();
  const { t } = useTranslation('createCustomer');

  const customerAddition = !customer.customerID;

  const hasRightsToAddCustomer = useSelector(getCanAddCustomers) === 1;
  const isTrespassing = !hasRightsToAddCustomer && customerAddition;
  useEffect(() => {
    if (isTrespassing) {
      dispatch(previousModalPage())
        // @ts-ignore
        .then(
          dispatch(
            addWarning(t(`alerts.cannotAddCustomer`), {
              selfDismiss: 3000,
            }),
          ),
        );
    }
  }, [dispatch, isTrespassing, t]);
  // prettier-ignore
  const customerRegistry = useSelector(getUseCustomerRegistry);
  const { customer_form_configuration: configuration } = useSelector(
    getGeneralCustomerSettings,
  );
  const allowCreateNewCompany = !useSelector(
    getSetting('brazil_disable_create_company'),
  );
  const prefillHomeStore = useSelector(getPrefillHomeStoreOnCustomerCreation);
  const warehouseID = useSelector(getSelectedWarehouseID);

  const [activeTab, setActiveTab] = useState<
    typeof CUSTOMER_TYPES[keyof typeof CUSTOMER_TYPES]
  >(
    customer.id && !newCustomer
      ? customer.customerType
      : configuration.allowedCustomerTypes || CUSTOMER_TYPES.PERSON,
  );
  const newCustomerHomeStore = prefillHomeStore
    ? Number(warehouseID)
    : undefined;

  const [PERSON, setPERSON] = useState({
    // new customer default data
    ...personInitState,
    // homestoreID for a new customer
    homeStoreID: newCustomerHomeStore,
    // if new customer - data for new customer
    // if existing customer - data from existing customer (overrides homeStoreID)
    ...(newCustomer ? newCustomerData : customer),
  });
  const [COMPANY, setCOMPANY] = useState({
    ...companyInitState,
    homeStoreID: newCustomerHomeStore,
    ...(newCustomer ? {} : customer),
  });
  const CUSTOMER = {
    [CUSTOMER_TYPES.PERSON]: PERSON,
    [CUSTOMER_TYPES.COMPANY]: COMPANY,
  }[activeTab];

  const [invalidFieldsPerCustomerType, setFormIsValid] = useState<
    Record<string, string[]>
  >({
    [CUSTOMER_TYPES.PERSON]: [],
    [CUSTOMER_TYPES.COMPANY]: [],
  });

  const setFieldIsValid = (fieldName: string, isValid: boolean) =>
    setFormIsValid(
      R.over(
        R.lensProp(activeTab),
        R.ifElse(() => isValid, R.without([fieldName]), R.union([fieldName])),
      ),
    );

  const newAddresses = newCustomerData?.addresses;

  useEffect(() => {
    const newCustomer = {
      addressTypeID: customer.addressTypeID,
      addresses: customer.addresses || newAddresses || [],
      newAddress: {},
    };
    const newCompany = {
      addressTypeID: customer.addressTypeID,
      addresses: customer.addresses || [],
      newAddress: {},
      birthday: '00-00-0000',
    };
    if (customerRegistry) {
      Object.assign(newCustomer, { gender: customer.gender });
      Object.assign(newCustomer, { euCustomerType: customer.euCustomerType });
      Object.assign(newCompany, { gender: customer.gender });
      Object.assign(newCompany, { euCustomerType: customer.euCustomerType });
    }
    setPERSON(p => ({ ...p, newCustomer }));
    setCOMPANY(c => ({ ...c, newCompany }));
  }, [customer, customerRegistry, newAddresses]);

  const setCustomer = useCallback(
    data =>
      activeTab === CUSTOMER_TYPES.PERSON ? setPERSON(data) : setCOMPANY(data),
    [activeTab, setPERSON, setCOMPANY],
  );

  const onSave = useCallback(() => {
    const emailOptOut = Number(CUSTOMER.emailOptOut);
    const signUpStoreID = newCustomer
      ? Number(warehouseID)
      : CUSTOMER.signUpStoreID;
    dispatch(
      saveCustomer(
        {
          ...CUSTOMER,
          emailOptOut,
          signUpStoreID,
        },
        () => {
          dispatch(
            openModalPage({
              component: mp.customerView,
              groupID: mp.customerView,
              replace: true,
            }),
          );
        },
      ),
    );
  }, [CUSTOMER, newCustomer, warehouseID, dispatch]);

  const closeCreateCustomer = useCallback(() => {
    if (CUSTOMER.id) {
      dispatch(
        openModalPage({
          component: mp.customerView,
          props: { customerId: CUSTOMER.id },
          groupID: mp.customerView,
          replace: true,
        }),
      );
    } else {
      dispatch(closeModalPage(mp.createCustomer));
    }
  }, [dispatch, CUSTOMER.id]);

  const customerState = CUSTOMER;
  const customerTypes = useMemo(() => {
    if (customerState.id) {
      return [customerState.customerType];
    }
    if (!configuration.allowedCustomerTypes.length) {
      return Object.values(CUSTOMER_TYPES);
    }
    return [configuration.allowedCustomerTypes];
  }, [
    customerState.id,
    customerState.customerType,
    configuration.allowedCustomerTypes,
  ]);

  return (
    <div
      className="create-customer-page"
      data-testid="create-customer-container"
    >
      <Modal.Header>
        <Modal.Title
          data-testid="title"
          style={{ fontSize: '24px', fontWeight: 700 }}
        >
          {t('title', { context: customerState.id ? 'edit' : 'create' })}
        </Modal.Title>
        <div className="create-customer-page-action-buttons">
          <SaveButton
            data-testid="save-button"
            action={onSave}
            disabled={!!invalidFieldsPerCustomerType[activeTab].length}
          />
          <CloseButton action={closeCreateCustomer} />
        </div>
      </Modal.Header>
      <Modal.Body>
        {allowCreateNewCompany && (
          <Nav variant="pills" fill>
            {customerTypes.map(el => (
              <Nav.Item key={el}>
                <Nav.Link
                  className={el === activeTab ? 'nav-link active' : 'nav-link'}
                  onClick={() => setActiveTab(el)}
                  eventKey={el}
                  data-testid={el}
                >
                  {t(`type.${el.toLowerCase()}`)}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}
        <CustomerForm
          onChange={setCustomer}
          customer={customerState}
          setFieldIsValid={setFieldIsValid}
          configuration={configuration}
        />
      </Modal.Body>
    </div>
  );
};

CreateCustomer.propTypes = {
  /** If true, displays a new customer form regardless of whether the POS has a customer already selected */
  newCustomer: PropTypes.bool,
  customer: PropTypes.object.isRequired,
};
export default CreateCustomer;
